import Router from 'next/router';
import { Component } from 'react';
import { ReplaySubject, Subscription, interval } from 'rxjs';
import { tap } from 'rxjs/operators';
import { PackageType, Packages } from '../../commons/commons';
import { ConvertOptionsContext, HTMLWidgetChangeEvent } from '../../components/convert-options/convert-options-context';
import ConvertOptionsComponent from '../../components/convert-options/convert-options.component';
import ContactUsModal from '../../components/ui/contact-us-modal/contact-us.modal';
import FileCardComponent from '../../components/ui/file-card/file-card.component';
import { MotiveComponent } from '../../components/ui/motive/motive.component';
import SortableComponent from '../../components/ui/sortable/sortable.component';
import { IOC_TYPES } from '../../context/ioc.types';
import { lazyInject } from '../../context/module';
import { DropboxFile } from '../../models/dropbox-file';
import { FileUpload } from '../../models/file-upload';
import { GoogleDriveFile } from '../../models/google-drive-file';
import { Logger } from '../../models/logger';
import { UploadInProgressFile } from '../../models/upload-in-progress-file';
import { EnvironmentService } from '../../services/environment.service';
import { FileManagerService } from '../../services/file-manager.service';
import { FileProcessService } from '../../services/file-process.service';
import { FileUploadService } from '../../services/file-upload.service';
import { LoginService } from '../../services/login.service';
import { SpinnerService } from '../../services/spinner.service';
import { UIContext } from '../../services/ui-context';
import { UploadedFilesService } from '../../services/uploaded-files.service';
import { WebSocketService } from '../../services/websocket.service';
import { WindowService } from '../../services/window.service';
import { ConvertOptionCategories } from '../../types/dropdown.type';
import { FileSelectEvent, FileUploadProps } from '../../types/file-upload.types';
import { PageData, RouteProps } from '../../types/route-types';
import { FromType, OrientationUpdateEvent, ProcessType, ToType, WebsocketResponse } from '../../types/types';
import { BrowserUtils } from '../../ui-utils/browser-utils';
import { FileUploadUtils } from '../../utils/file-upoad.utils';
import { cloneDeep, get } from '../../utils/lodash-min';
import { reloadAfterExecute } from '../../utils/react.utils';
import { MotiveConfig, MotiveLocationTypes } from '../../utils/request.utils';
import { RxUtils } from '../../utils/rx-utils';
import { untilDestroyed } from '../../utils/until-destroyed';
import Tooltip from '../ui/tooltip/tooltip.component';
import styles from './file-convert.component.module.scss';
import { FileUploadManager, SupportedFileTypes } from './file-upload-manager';
import FileUploadComponent from './file-upload/file-upload.component';

interface FileConvertComponentState {
    fileWithMetadataList: UploadInProgressFile<DropboxFile | File | GoogleDriveFile>[],
    fileSelectedByUser: boolean,
    fileSelectBtnClickedByUser: boolean,
    fakeUploadInProgressFileForSelectedFiles: Array<UploadInProgressFile<any>>,
    sideMenuShown: boolean;
    showMainUploadButton: boolean;
}

export default class FileConvertComponent extends Component<RouteProps, FileConvertComponentState> {
    logger = Logger.getLogger('FileConvertComponent');

    private title: string;
    private description: string;
    public packageType: PackageType = null;
    public get isPaidUser() {
        return !!this.packageType;
    }
    public get isProUser() {
        return this.isPaidUser ? this.packageType === Packages.PACKAGE_TYPE_PRO : false;
    }
    public avMaxFileLimit = 10;
    public nonAVMaxFileLimit = 10;

    public processType: ProcessType;
    public fromType: Array<FromType>;
    public toType: ToType;
    public accept: string;
    public fileUploadBtnText: string = 'Select Files';
    public btnText: string;
    public selectedOutputExtension: any;
    public convertOptionsContext: ConvertOptionsContext;

    public convertSubscriber$: Subscription;
    private subscribersToClose: Subscription[] = [];
    public startConvertProcessTriggered = false;
    public convertProcessStartRequestProcessed = false;

    private hasFileUploads = false;

    effectiveMotives: MotiveConfig;
    // private fileUploadArray: FileUpload[];

    // @Output() 
    onSidePanelFileUploadsChange = new ReplaySubject<FileUpload[]>();

    fileUploadManager: FileUploadManager;

    sortableOptions = {
        onUpdate: (event: any) => {
            this.sortOrderChanged(event);
        },
    };

    wsConnected = true;

    get shouldShowLoading() {
        return this.state.fakeUploadInProgressFileForSelectedFiles.length > 0;
    }

    get getLoadingFileList() {
        return Array.from(this.state.fakeUploadInProgressFileForSelectedFiles);
    }

    @lazyInject(IOC_TYPES.UIContext) private uiContext: UIContext;
    @lazyInject(IOC_TYPES.UploadedFilesService) private uploadedFilesService: UploadedFilesService;
    @lazyInject(IOC_TYPES.WebSocketService) public websocketService: WebSocketService; // Template used this
    @lazyInject(IOC_TYPES.FileProcessService) public fileProcessService: FileProcessService;
    @lazyInject(IOC_TYPES.WindowService) public windowService: WindowService;
    @lazyInject(IOC_TYPES.FileManagerService) private fileManagerService: FileManagerService;
    @lazyInject(IOC_TYPES.SpinnerService) private spinnerService: SpinnerService;
    @lazyInject(IOC_TYPES.EnvironmentService) private environmentService: EnvironmentService;
    @lazyInject(IOC_TYPES.LoginService) private loginService: LoginService;
    @lazyInject(IOC_TYPES.FileUploadService) private fileUploadService: FileUploadService;


    // sensors = useSensors(
    //     useSensor(PointerSensor),
    //     useSensor(KeyboardSensor, {
    //       coordinateGetter: sortableKeyboardCoordinates,
    //     })
    //   );

    readonly state: Readonly<FileConvertComponentState> = {
        fileWithMetadataList: [],
        fileSelectedByUser: false,
        fakeUploadInProgressFileForSelectedFiles: [],
        sideMenuShown: false,
        showMainUploadButton: true,
        fileSelectBtnClickedByUser: false,
    };
    constructor(props: RouteProps) {
        super(props);
        const wsStatus = this.websocketService.status.pipe(
            untilDestroyed(this),
        ).subscribe({
            next: () => {
                this.wsConnected = this.websocketService.isConntected() && this.websocketService.isServiceReady();
            }
        });
        this.subscribersToClose.push(wsStatus);
        this.convertOptionsContext = ConvertOptionsContext.newContext(props.convertOptionCategories);
        const optionChangedSub = this.convertOptionsContext.onSelectedOptionsChanged.subscribe({
            next: (event) => {
                this.convertOptionCategoriesChanged(event);
            }
        });
        this.subscribersToClose.push(optionChangedSub);

        const userDetailsChangedSub = this.loginService.onUserDetailsChanged.subscribe({
            next: (userDetails) => {
                this.packageType = userDetails && userDetails.isPaidUser() ? userDetails.packageType : null;
                this.nonAVMaxFileLimit = this.packageType === Packages.PACKAGE_TYPE_PRO ? 25 :
                    (this.packageType === Packages.PACKAGE_TYPE_BASIC ? 10 : 5);
                this.avMaxFileLimit = this.packageType === Packages.PACKAGE_TYPE_PRO ? 25 :
                    (this.packageType === Packages.PACKAGE_TYPE_BASIC ? 10 : 5);
            }
        });
        this.subscribersToClose.push(userDetailsChangedSub);

        // const fileSelectedSub = this.fileUploadService.onFileSelected.subscribe({
        //     next: () => {
        //         this.fileSelectedByUser();
        //     }
        // });
        // this.subscribersToClose.push(fileSelectedSub);

        // const fileSelectBtnClickedSub = this.windowService.onFileSelectBtnClicked.subscribe({
        //     next: () => {
        //         this.fileSelectButtonClicked();
        //     }
        // });
        // this.subscribersToClose.push(fileSelectBtnClickedSub);


        this.processPageData(props.pageData);
    }

    componentWillUnmount() {
        this.convertOptionsContext.destroy();
        this.subscribersToClose.forEach(sub => {
            RxUtils.safeCloseSubscriber(sub);
        });
    }

    private processPageData(value: PageData) {
        this.title = value.page_seo.title;
        this.description = value.page_seo.description && value.page_seo.description.replace(/\s+/g, ' ');
        this.processType = value.processType;
        this.fromType = value.fromType;
        this.toType = value.toType;
        this.accept = value.accept;
        this.btnText = value.btnText;
        this.fileUploadBtnText = value.file_uload_button_text
    }

    componentDidMount() {
        this.setState({
            fileSelectedByUser: false,
        });
        const fileReloadSub = this.uploadedFilesService.getOnRequestFileReuploadSubscriber().pipe(
            untilDestroyed(this),
        ).subscribe({
            next: (fileUpload: FileUpload) => {
                this.fileSelected({
                    files: fileUpload.file,
                } as any).catch(console.error);
                this.uploadedFilesService.removeFileFromFileList(fileUpload);
                this.forceUpdate();
            },
            error: (err) => {
                this.logger.error({ message: 'Failed to load PageData', error: err });
                this.forceUpdate();
            },
        });
        this.subscribersToClose.push(fileReloadSub);

        // this.populateTestImageForUpload();
    }

    private populateTestImageForUpload() {
        this.setState({
            fileSelectedByUser: true,
            fileSelectBtnClickedByUser: true,
        });
        setTimeout(async () => {
            const response = await fetch("https://www.xconvert.com/img/xconvert/desktop/windows/screen2.png");
            // here image is url/location of image
            const blob = await response.blob();
            const file = new File([blob], 'image.png', { type: blob.type });
            const file1 = new File([blob], 'Long image name for testing.png', { type: blob.type });
            const file2 = new File([blob], 'This is another image. Little big longer than the previous.png', { type: blob.type });
            this.fileSelected({ files: [file, file1, file2] });
        }, 1000);
    }

    public convertWhenReady() {
        if (!this.startConvertProcessTriggered) {
            if (this.hasProcessUploadedFiles()) {
                this.startConvertProcessTriggered = true;
                this.convertSubscriber$ = interval(1000).subscribe({
                    next: () => {
                        this.startConvertProcess();
                    },
                });
                this.uiContext.successToast(`File ${this.processType} will automatically start when upload complete!`, '', 10000);
            } else {
                this.uiContext.errorToast(`Please select files!`, '', false, false);
            }
        } else {
            this.uiContext.successToast(`Process will start once all files are uploaded!`, '', 10000);
        }
        this.forceUpdate();
    }

    public startConvertProcess() {
        if (this.hasProcessUploadedFiles() && this.canProcessUploadedFiles()) {
            if (!this.convertProcessStartRequestProcessed) {
                this.convertProcessStartRequestProcessed = true;
                this.resetProcessOnUploadCompleteProcedure();
                this.convert().catch(console.error);;
            }
        }
    }

    public updateProcessProbedDataFn() {
        const that = this;
        return async (probeDuration: number) => {
            if (that.processType !== 'merge') {
                const fileUpload = that.convertMergeUploadInProgressFilesToFileUploads();
                return await that.fileProcessService.probeProcessForInfo(fileUpload, probeDuration);
            }
        };
    }

    @reloadAfterExecute()
    private resetProcessOnUploadCompleteProcedure() {
        RxUtils.safeCloseSubscriber(this.convertSubscriber$);
        this.startConvertProcessTriggered = false;
        this.convertProcessStartRequestProcessed = false;
    }

    public async convert() {
        // console.log(this.convertNoneMergeUploadInProgressFilesToFileUploads())
        // return
        let shouldNavigateToDownloadPage = true;
        if (this.state.fileWithMetadataList.length > 0 && this.canProcessUploadedFiles()) {
            if (this.processType === 'merge') {
                RxUtils.safeCloseSubscriber(this.convertSubscriber$);
                const fileUpload = this.convertMergeUploadInProgressFilesToFileUploads();
                this.logger.trace7({ message: 'Request to merge files.', data: FileUpload.toLoggableObject(fileUpload) });
                this.fileManagerService.addFileUpload(fileUpload);
                // this.fileUploadArray.unshift(fileUpload);
                // this.fileUploadService.notify(fileUpload);
                await this.fileProcessService.processFile(fileUpload).pipe(
                    tap({
                        next: (resp) => {
                            this.logger.trace7({ message: 'Success file process.', data: resp });
                        },
                        error: (err) => {
                            this.logger.error({
                                message: 'File process failed.',
                                error: err,
                                data: FileUpload.toLoggableObject(fileUpload),
                            });
                            this.uiContext.showErrorDialog({ title: 'Error', text: `Failed to ${this.processType} the file. Please try using XConvert on Chrome incognito mode.` });
                        }
                    })
                ).toPromise();
                // TODO probably remove this since we are not using router with Preact, 
                // we need to wait for process file to resolve before changing to downlod page
                // .subscribe({
                //     next: (resp) => {
                //         this.logger.trace7({ message: 'Success file process.', data: resp });
                //     },
                //     error: (err) => {
                //         this.logger.error({
                //             message: 'File process failed.',
                //             error: err,
                //             data: FileUpload.toLoggableObject(fileUpload),
                //         });
                //         this.uiContext.showErrorDialog({ title: 'Error', text: `Failed to ${this.processType} the file. Please try using XConvert on Chrome incognito mode.` });
                //     }
                // });
            } else {
                let fileUploads: FileUpload[] = this.convertNoneMergeUploadInProgressFilesToFileUploads();
                const { shouldContinue } = await this.imposeLimitsAndRemoveExcessFileUploadsIfNeeded(fileUploads);
                if (!shouldContinue) {
                    return;
                }

                const instance = await this.spinnerService.show('Starting to process your request...');
                const allPromises = [];
                try {
                    let index = 0;
                    for (const fileUpload of fileUploads) {
                        this.spinnerService.updateMessage(instance, `Submitted ${fileUploads.indexOf(fileUpload)}/${fileUploads.length} files. Submitting file ${fileUpload.name} to process...`);
                        this.fileManagerService.addFileUpload(fileUpload);
                        // this.fileUploadArray.unshift(fileUpload);
                        // this.fileUploadService.notify(fileUpload);
                        const processPromise = this.fileProcessService.processFile(fileUpload).pipe(
                            tap({
                                next: (resp) => {
                                    this.logger.trace7({ message: 'Success file process.', data: resp });
                                },
                                error: (err) => {
                                    this.logger.error({
                                        message: 'File process failed.',
                                        error: err,
                                        data: FileUpload.toLoggableObject(fileUpload),
                                    });
                                    this.uiContext.showErrorDialog({ title: 'Error', text: `Failed to ${this.processType} the file. Please try using XConvert on Chrome incognito mode.` });
                                }
                            }),
                        ).toPromise();
                        allPromises.push(processPromise);
                        // TODO probably remove this since we are not using router with Preact, 
                        // we need to wait for process file to resolve before changing to downlod page
                        // .subscribe({
                        //     next: (resp) => {
                        //         this.logger.trace7({ message: 'Success file process.', data: resp });
                        //     },
                        //     error: (err) => {
                        //         this.logger.error({
                        //             message: 'File process failed.',
                        //             error: err,
                        //             data: FileUpload.toLoggableObject(fileUpload),
                        //         });
                        //         this.uiContext.showErrorDialog({ title: 'Error', text: `Failed to ${this.processType} the file. Please try using XConvert on Chrome incognito mode.` });
                        //     }
                        // });
                        if (!index) {
                            this.spinnerService.updateMessage(instance, `Successfully submitted ${fileUpload.name} for processing...`);
                        } else if ((index > 0 && index < (fileUploads.length - 1)) && this.isAVType(fileUpload.toType)) {
                            this.spinnerService.updateMessage(instance, `Successfully submitted ${fileUpload.name} for processing. Submitting next process in 3 seconds...`);
                            await new Promise((resolve) => setTimeout(() => resolve(null), 3000));
                        }

                        index = index + 1;
                    }
                    await Promise.all(allPromises);
                } catch (err) {
                    shouldNavigateToDownloadPage = false;
                    // suppress
                    try {
                        const fileUploadsForLoggin: FileUpload[] = this.convertNoneMergeUploadInProgressFilesToFileUploads().map(fileUpload => FileUpload.toLoggableObject(fileUpload));
                        this.logger.error({
                            message: 'File process failed during submittion.',
                            error: err,
                            data: fileUploadsForLoggin,
                        });
                    } catch (err) {
                        // suppress
                    }
                } finally {
                    this.spinnerService.hide(instance);
                }
            }
            if (shouldNavigateToDownloadPage) {
                // const router = useRouter();
                Router.push('/downloads');
            }
        } else {
            const errTitle = this.hasProcessUploadedFiles() ? 'Still Uploading' : 'No Files To Convert';
            const errMsg = this.hasProcessUploadedFiles() ? 'Wait till upload finishes' : 'First select files to convert.';
            this.uiContext.errorToast(errTitle, errMsg, false, true);
        }
    }

    private async imposeLimitsAndRemoveExcessFileUploadsIfNeeded(fileUploads: FileUpload[]) {
        const isAVType = this.isAVType(fileUploads[0].toType);
        const maxFileLimit = isAVType ? this.avMaxFileLimit : 1200; // use this.nonAVMaxFileLimit when there are lots of users
        let shouldContinue = true;
        if (fileUploads.length > maxFileLimit) {
            const userTypeMessage = this.isPaidUser ? (this.isProUser ? 'Pro' : 'Basic') : 'Free';
            const message = 'Concurrent process limit exceeded for '
                + userTypeMessage
                + ' user account. '
                + userTypeMessage
                + ' account user max limit for file type '
                + fileUploads[0].toType
                + ' is '
                + maxFileLimit
                + ". You are trying to process "
                + fileUploads.length
                + ". You may dismiss this message and remove excess files or continue to process only first "
                + maxFileLimit
                + " files.";
            shouldContinue = await new Promise(resolve => {
                this.uiContext.showConfirmationWarningDialog(
                    'Limit Exceeded',
                    message,
                    () => {
                        fileUploads.splice(0, maxFileLimit)
                        resolve(true);
                    },
                    true,
                    "Dismiss",
                    "Continue",
                    () => {
                        resolve(false);
                    }
                );
            });
        }
        return { shouldContinue, fileUploads };
    }

    private isAVType(toType: ToType) {
        return ['av', 'video', 'mp4', 'webm', 'mov', 'mkv', 'mts'].indexOf(toType) !== -1;
    }

    convertMergeUploadInProgressFilesToFileUploads(): FileUpload {
        const optionsSnapshot: ConvertOptionCategories = cloneDeep(this.convertOptionsContext.getConvertOptionCategories());

        const options = [];
        options.push(...this.convertOptionsContext.toConvertOptionsSummary());
        const orientationAngleList: string[] =
            this.state.fileWithMetadataList.map(fm => (fm.getOrientation() && fm.getOrientation().angle) || '0');
        const orientationFlipList: string[] =
            this.state.fileWithMetadataList.map(fm => (fm.getOrientation() && fm.getOrientation().isFliped) || 'false');
        options.push(
            { formName: 'orientationAngleList', formValue: orientationAngleList },
            { formName: 'orientationFlipList', formValue: orientationFlipList },
        );
        const fileUploads: FileUpload[] = this.state.fileWithMetadataList.map(fmd => {
            return FileUploadUtils.toFileUpload(
                fmd,
                this.websocketService.getId(),
                this.processType,
                this.fromType,
                this.toType,
                options,
                optionsSnapshot,
                fmd.uniqueProcessType,
                this.props.pageData
            );
        });

        const fileUpload = fileUploads.splice(0, 1)[0];
        fileUpload.metadataList = [cloneDeep(fileUpload.metadata)]; // now, fileUploads doesnt have this
        fileUploads.forEach(fu => {
            fileUpload.originalInputFiles.push(...fu.originalInputFiles);
            fileUpload.metadataList.push(cloneDeep(fu.metadata));
        });

        return fileUpload;
    }

    convertNoneMergeUploadInProgressFilesToFileUploads(): FileUpload[] {
        return this.state.fileWithMetadataList.map(fmd => {
            return this.convertNoneMergeUploadInProgressFilesToFileUpload(fmd);
        });
    }

    convertNoneMergeUploadInProgressFilesToFileUpload(fmd: UploadInProgressFile<SupportedFileTypes>): FileUpload {
        const optionsSnapshot: ConvertOptionCategories = cloneDeep(this.convertOptionsContext.getConvertOptionCategories());

        const options = [];
        options.push(...this.convertOptionsContext.toConvertOptionsSummary());
        const orientationAngleList: string[] = [(fmd.getOrientation() && fmd.getOrientation().angle) || '0'];
        const orientationFlipList: string[] = [(fmd.getOrientation() && fmd.getOrientation().isFliped) || 'false'];
        options.push(
            { formName: 'orientationAngleList', formValue: orientationAngleList },
            { formName: 'orientationFlipList', formValue: orientationFlipList },
        );

        const fileUpload = FileUploadUtils.toFileUpload(
            fmd,
            this.websocketService.getId(),
            this.processType,
            this.fromType,
            this.toType,
            options,
            optionsSnapshot,
            fmd.uniqueProcessType,
            this.props.pageData,
        );

        fileUpload.metadataList = [cloneDeep(fileUpload.metadata)];
        return fileUpload;
    }

    /**
     * Does not delete fake uploadInProgress files
     * @param file 
     */
    removeFile(file) {
        let index = -1;
        this.state.fileWithMetadataList.forEach((rowFileAndData, i) => {
            if (rowFileAndData.getFile() === file) {
                index = i;
            }
        });
        if (index !== -1) {
            this.state.fileWithMetadataList.splice(index, 1);
        }
        this.forceUpdate();
    }

    hasProcessUploadedFiles() {
        return this.state.fileWithMetadataList.length > 0;
    }

    canProcessUploadedFiles() {
        return this.hasProcessUploadedFiles() && this.state.fileWithMetadataList.every(f => f.getComplete());
    }

    // fileSelectedByUser() {
    //     this.setState({
    //         fileSelectedByUser: true,
    //     });
    // }

    // fileSelectButtonClicked() {
    //     this.setState({
    //         fileSelectBtnClickedByUser: true,
    //     });
    //     this.hideMainUploadButton();
    //     this.fileSelectedByUser();
    // }

    async fileSelected(event: FileSelectEvent) {
        if (!this.fileUploadManager) {
            // IMPORTANT: This need to be re-initialized if moved to SPA
            this.fileUploadManager = new FileUploadManager(
                this.state.fakeUploadInProgressFileForSelectedFiles,
                this.state.fileWithMetadataList,
                this.props,
                this.logger,
                this.fromType,
                this.toType,
                () => {
                    this.forceUpdate();
                },
                () => {
                    this.updateSidePanelFileUploads();
                    this.forceUpdate();
                },
            );
            this.fileUploadManager.fileListChanged.subscribe((fileListChangeEvent) => {
                this.hasFileUploads = get(fileListChangeEvent, 'fileWithMetadataList', []).length > 0;
                this.forceUpdate();
            });
        }
        await this.fileUploadManager.fileSelected(event);
    }


    orientationChanged(orientationData: OrientationUpdateEvent) {
        const uploadInProgressFile = this.state.fileWithMetadataList.find(fm => fm && fm.getFile() === orientationData.file);
        if (!uploadInProgressFile) {
            this.logger.trace1('Set orientation was called on a file that is not on uploadInProgressFiles list');
            return;
        }
        uploadInProgressFile.setOrientation(orientationData.orientation);
        this.updateSidePanelFileUploads();
        this.forceUpdate();
    }

    sortOrderChanged(e) {
        this.updateSidePanelFileUploads();
    }

    sortEnded(e) {
        this.state.fileWithMetadataList.splice(0, this.state.fileWithMetadataList.length, ...e);
        this.updateSidePanelFileUploads();
        this.forceUpdate();
    }

    convertOptionCategoriesChanged(event: HTMLWidgetChangeEvent) {
        if (event && event.selectedValue && event.formChild && event.formChild.ctxCallbackFn) {
            event.formChild.ctxCallbackFn(this, event.selectedValue.formName);
        }

        this.updateSidePanelFileUploads();
    }

    updateSidePanelFileUploads() {
        if (true) {
            return;
        }
        // let sidePanelFileUploads = [];
        // const shouldContinue = !this.state.fileWithMetadataList.find(fml => !fml.completed);
        // if (this.state.fileWithMetadataList.length > 0 && shouldContinue) {
        //     if (this.processType === 'merge') {
        //         const fileUpload = this.convertMergeUploadInProgressFilesToFileUploads();
        //         sidePanelFileUploads = [fileUpload];
        //         this.onSidePanelFileUploadsChange.next(sidePanelFileUploads);
        //     } else {
        //         const fileUploads: FileUpload[] = this.convertNoneMergeUploadInProgressFilesToFileUploads();
        //         sidePanelFileUploads = fileUploads;
        //         this.onSidePanelFileUploadsChange.next(sidePanelFileUploads);
        //     }
        // }
    }

    decodeData(wsData: WebsocketResponse) {
        if (wsData.dataType === 'notification') {
            return wsData.data;
        } else if (wsData.dataType === 'ratio') {
            return wsData.data;
        } else if (wsData.dataType === 'percentage') {
            return parseFloat(wsData.data).toFixed(2);
        } else {
            return 'Waiting for server to reply';
        }
    }

    showContactUs(e: MouseEvent) {
        e.preventDefault();
        ContactUsModal.show({});
    }

    getFileConverts() {
        const fileUploadProps: FileUploadProps = {
            acceptedFileTypes: this.accept || '*',
            uploadBtnText: 'Add',
            fileSelectButtonClicked: () => {
                // Skipped
            },
            fileSelected: (ev) => {
                this.fileSelected(ev).catch((e) => {
                    this.logger.error({
                        message: "fileSelected failed",
                        error: e
                    })
                });
                // this.hideMainUploadButton();
            },
        };


        if (true) {
            return <div className={styles['sortable-container']}>
                <SortableComponent items={this.state.fileWithMetadataList}
                    sortHandlerClass='sort-handler'
                    classes={['row']}
                    orderChanged={(e) => this.sortOrderChanged(e)}
                    sortEnded={(e) => this.sortEnded(e)}>
                    {
                        this.state.fileWithMetadataList.filter(f => f).map(uploadInProgressFile => {
                            return <div key={`${uploadInProgressFile.uuid}-parent`}
                                style={{}}
                                data-notsortable={false}
                                data-uniquekey={uploadInProgressFile.uuid}
                                className={`${styles['sortable']} ${uploadInProgressFile.file.type.indexOf('image') === -1 ? 'non-image' : 'image'}`}
                            >
                                <FileCardComponent
                                    {...this.props}
                                    key={uploadInProgressFile.uuid} // disabled since in React 19 Component missing `key` property
                                    uploadInProgressFile={uploadInProgressFile}
                                    convertOptionsContext={this.convertOptionsContext}
                                    convertOptionCategories={this.convertOptionsContext.getConvertOptionCategories()}
                                    updateProcessProbedDataFnProvider={this}
                                    toType={this.toType}
                                    fromType={this.fromType}
                                    removeFile={$event => this.removeFile($event)}
                                    orientationChanged={$event => this.orientationChanged($event)}>
                                </FileCardComponent>
                            </div>
                        })
                    }
                </SortableComponent>
            </div>
        }

        if (this.shouldShowLoading) {
            return <div className={styles['in-progress-files-list']}>
                {
                    <div className={styles['non-sortable-item']} data-notsortable={true}>
                        <div className={styles['upload-text-container']}>
                            <FileUploadComponent {...fileUploadProps} displayBtnsInAGridWithLargeBtnOnTop={true}></FileUploadComponent>
                        </div>
                    </div>
                }
                {
                    this.getLoadingFileList.map(file => {
                        return <div key={file.uuid}>
                            <FileCardComponent
                                {...this.props}
                                uploadInProgressFile={file}
                                convertOptionCategories={this.convertOptionsContext.getConvertOptionCategories()}
                                convertOptionsContext={this.convertOptionsContext}
                                updateProcessProbedDataFnProvider={this}
                                toType={this.toType}
                                fromType={this.fromType}
                                removeFile={$event => this.removeFile($event)}
                                orientationChanged={$event => this.orientationChanged($event)}>
                            </FileCardComponent>
                        </div>
                    })
                }
            </div>
        } else if (this.state.fileWithMetadataList && this.state.fileWithMetadataList.length != 0) {
            if (true) {
                return <div>
                    <SortableComponent items={this.state.fileWithMetadataList}
                        sortHandlerClass='sort-handler'
                        classes={['row']}
                        orderChanged={(e) => this.sortOrderChanged(e)}
                        sortEnded={(e) => this.sortEnded(e)}>
                        {
                            this.state.fileWithMetadataList.map(uploadInProgressFile => {
                                return <div key={uploadInProgressFile.uuid}
                                    style={{}}
                                    data-notsortable={false}
                                    data-uniquekey={uploadInProgressFile.uuid}
                                    className={`${styles['sortable']} ${uploadInProgressFile.file.type.indexOf('image') === -1 ? 'non-image' : 'image'}`}
                                >
                                    <FileCardComponent
                                        key={uploadInProgressFile.uuid} // disabled since in React 19 Component missing `key` property
                                        {...this.props}
                                        uploadInProgressFile={uploadInProgressFile}
                                        convertOptionsContext={this.convertOptionsContext}
                                        convertOptionCategories={this.convertOptionsContext.getConvertOptionCategories()}
                                        updateProcessProbedDataFnProvider={this}
                                        toType={this.toType}
                                        fromType={this.fromType}
                                        removeFile={$event => this.removeFile($event)}
                                        orientationChanged={$event => this.orientationChanged($event)}>
                                    </FileCardComponent>
                                </div>
                            })
                        }
                    </SortableComponent>
                </div>
            }

            return <div id="files-and-upload-btn-container">
                <div>
                    {
                        <SortableComponent items={this.state.fileWithMetadataList}
                            sortHandlerClass='sort-handler'
                            orderChanged={(e) => this.sortOrderChanged(e)}
                            sortEnded={(e) => this.sortEnded(e)}>
                            {
                                <div key="non-sortable-item" className={styles['non-sortable-item']} data-notsortable={true}>
                                    <div className={styles['upload-text-container']}>
                                        <FileUploadComponent {...fileUploadProps} displayBtnsInAGridWithLargeBtnOnTop={true}></FileUploadComponent>
                                    </div>
                                </div>
                            }
                            {
                                this.state.fileWithMetadataList.map(uploadInProgressFile => {
                                    return <div key={uploadInProgressFile.uuid}
                                        data-notsortable={false}
                                        data-uniquekey={uploadInProgressFile.uuid} className={`a${uploadInProgressFile.file.name} ${styles['sortable']} ${uploadInProgressFile.file.type.indexOf('image') === -1 ? 'non-image' : 'image'}`}
                                    >
                                        <FileCardComponent
                                            key={uploadInProgressFile.uuid} // disabled since in React 19 Component missing `key` property
                                            {...this.props}
                                            uploadInProgressFile={uploadInProgressFile}
                                            convertOptionsContext={this.convertOptionsContext}
                                            convertOptionCategories={this.convertOptionsContext.getConvertOptionCategories()}
                                            updateProcessProbedDataFnProvider={this}
                                            toType={this.toType}
                                            fromType={this.fromType}
                                            removeFile={$event => this.removeFile($event)}
                                            orientationChanged={$event => this.orientationChanged($event)}>
                                        </FileCardComponent>
                                    </div>
                                })
                            }
                        </SortableComponent>

                    }
                </div>
            </div>
        } else if (this.state.fileSelectBtnClickedByUser) {
            return <div id="upload-btn-container" className={styles['non-sortable-item']} data-notsortable={true}>
                <div className={styles['upload-text-container']}>
                    <FileUploadComponent {...fileUploadProps} displayBtnsInAGridWithLargeBtnOnTop={true}></FileUploadComponent>
                </div>
            </div>;
        }
    }

    private isXConvert() {
        return this.environmentService && this.environmentService.isXConvert();
    }

    private toggleSideMenu() {
        this.setState({
            sideMenuShown: !this.state.sideMenuShown,
        });
    }

    private hideMainUploadButton() {
        if (this.state.showMainUploadButton) {
            this.setState({
                showMainUploadButton: !this.state.showMainUploadButton,
            });

            setTimeout(() => {
                BrowserUtils.runIfWindowExists(() => {
                    if (window.innerWidth < 500) {
                        this.toggleSideMenu();
                    }
                });
            })
        }
    }

    handleDragEnd(event) {
        const { active, over } = event;

        if (active.id !== over.id) {
            //   setItems((items) => {
            //     const oldIndex = items.indexOf(active.id);
            //     const newIndex = items.indexOf(over.id);

            //     return arrayMove(items, oldIndex, newIndex);
            //   });
        }
    }

    render() {
        const extraProps = {
            convertOptionCategories: this.convertOptionsContext.getConvertOptionCategories(),
            convertOptionsContext: this.convertOptionsContext,
            uploadInProgressFiles: [
                ...this.state.fakeUploadInProgressFileForSelectedFiles,
                ...this.state.fileWithMetadataList
            ],
            onOptionChange: (data) => {
                // Skipped
            },
        };
        const fileUploadProps: FileUploadProps = {
            acceptedFileTypes: this.accept || '*',
            uploadBtnText: this.fileUploadBtnText || 'Add Files',
            fileSelectButtonClicked: () => {
                // Skipped
            },
            fileSelected: (ev) => {
                this.fileSelected(ev).catch(console.error);
                this.hideMainUploadButton();
            },
        };


        if (true) {
            return <>
                <div className='mt-2'>
                    <div className={styles['feature-details']}>
                        <h1 className={`${styles['title']} text-center`}>
                            {this.title}
                        </h1>
                        <div className={`${styles['description']} secondary-text text-center`}
                        dangerouslySetInnerHTML={{ __html: this.description }}></div>
                    </div>
                    <div className="gcontent-container feature-top">
                        {
                            <MotiveComponent
                                data="9534307497"
                                containerWidth="100%"
                                containerHeight="60px"
                                location={MotiveLocationTypes.convertPageBelowFeatureTitle}
                                adWidth="720px"
                                adHeight="60px"
                                showAds={true}
                                className='width_major_md'
                            ></MotiveComponent>
                        }
                    </div>
                    {
                        this.getFileConverts()
                    }
                    <div className='row' key={'file-convert-file-upload-container'}>
                        <div className='col-12'>
                            <div className="card border-0">
                                <div className="card-body p-0 bg-pattern" style={{}}>
                                    <div className={`row`}>
                                        <div className='col-sm-12 col-md-5'>
                                            <div style={{ margin: '1rem', backgroundColor: '#0000001c' }}>
                                                <FileUploadComponent {...fileUploadProps} shouldDisplayBtnsAsPrimary={true}></FileUploadComponent>
                                            </div>

                                        </div>
                                        <div className='col-sm-12 col-md-3'></div>
                                        <div className='col-sm-12 col-md-4'>
                                            <button className={`btn btn-lg btn-primary square-edges pull-right w-100`}
                                                onClick={() => this.convertWhenReady()}
                                                style={{ padding: "1.2rem 2rem" }}>
                                                {
                                                    this.startConvertProcessTriggered ?
                                                        'Loading...' : <span>{(this.btnText || ((this.processType || 'Process')))}
                                                            <img width={"16px"} height={"16px"} style={{ width: '1rem', marginLeft: '0.5rem' }}
                                                                className="color-invert"
                                                                src="/icons/svg/arrow-right-short-shaft.svg"
                                                                alt='Start'
                                                            /></span>
                                                }
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className='col-12' style={{}}>
                            <div className="card mt-3">
                                <div className="card-header">
                                    <div className="fs-5">
                                        <img width={"16px"} height={"16px"}
                                        style={{ width: '1rem', verticalAlign: 'unset' }} className="color-invert me-1"
                                         src="/icons/svg/gear.svg" alt='Options'/><b>ADVANCED OPTIONS</b>
                                    </div>
                                    <span><strong>Default values are recommended for most users.</strong> But, you may fine-tune your media files with these optional settings.</span>
                                </div>
                                <div className="card-body">
                                    {/* <div className="gcontent-container feature-top">
                                        {
                                            <MotiveComponent
                                                data="9534307497"
                                                containerWidth="100%"
                                                containerHeight="60px"
                                                location={MotiveLocationTypes.convertPageBottom}
                                                adWidth="720px"
                                                adHeight="60px"
                                                showAds={true}
                                                className='width_major_md'
                                            ></MotiveComponent>
                                        }
                                    </div> */}
                                    {/* {
                                        <ConvertOptionsComponentV2
                                        {...this.props} {...extraProps}
                                        ></ConvertOptionsComponentV2>
                                    } */}
                                    {
                                        <ConvertOptionsComponent
                                            {...this.props} {...extraProps}></ConvertOptionsComponent>
                                    }
                                    
                                </div>
                            </div>


                        </div>
                    </div>
                </div>
            </>
        }

        const noFileUploadStyle = !this.state.fileSelectedByUser ? styles['no-file-uploaded'] : '';
        return <div className={`${styles['file-convert-container']} ${this.state.sideMenuShown ? styles['options-hidden'] : ''}`}>
            <div className={`${styles['file-uploads-container']} ${noFileUploadStyle}`}>
                {
                    (this.state.showMainUploadButton || true) && <div>
                        <div className={styles['feature-details']}>
                            <h1 className={styles['title']}><b>{this.title}</b></h1>
                            <p className={`${styles['description']} secondary-text`}>{this.description}</p>
                        </div>
                        <div className="gcontent-container feature-top">
                            {
                                <MotiveComponent
                                    data="9534307497"
                                    containerWidth="100%"
                                    containerHeight="60px"
                                    location={MotiveLocationTypes.convertPageBelowFeatureTitle}
                                    adWidth="720px"
                                    adHeight="60px"
                                    showAds={true}
                                    className='width_major_md'
                                ></MotiveComponent>
                            }
                        </div>
                        {
                            this.getFileConverts()
                        }
                        <div className={`row`} style={{ backgroundColor: '#0000001c' }}>
                            <div className='col-sm-12 col-md-4'>
                                <div style={{ margin: '1rem' }}>
                                    <FileUploadComponent {...fileUploadProps} shouldDisplayBtnsAsPrimary={true}></FileUploadComponent>
                                </div>

                            </div>
                            <div className='col-sm-12 col-md-4'></div>
                            <div className='col-sm-12 col-md-4'>
                                <button className={`btn btn-lg btn-primary square-edges pull-right w-100`}
                                    onClick={() => this.convertWhenReady()}
                                    style={{ padding: "1.2rem 2rem" }}>
                                    {
                                        this.startConvertProcessTriggered ?
                                            'Loading...' : <span>{(this.btnText || ((this.processType || 'Process')))}
                                                <img style={{ width: '1rem', marginLeft: '0.5rem' }} className="color-invert" src="/icons/svg/arrow-right-short-shaft.svg" /></span>
                                    }
                                </button>
                            </div>
                        </div>
                        {
                            <ConvertOptionsComponent
                                {...this.props} {...extraProps}></ConvertOptionsComponent>
                        }
                    </div>
                }
                {
                    !this.state.showMainUploadButton && <div className="gcontent-container feature-top">
                        {
                            <MotiveComponent
                                data="9534307497"
                                containerWidth="100%"
                                containerHeight="60px"
                                location={MotiveLocationTypes.convertPageBelowFeatureTitle}
                                adWidth="720px"
                                adHeight="60px"
                                showAds={true}
                                className='width_major_md'
                            ></MotiveComponent>
                        }
                    </div>
                }
                {
                    this.getFileConverts()
                }
                {
                    !this.state.showMainUploadButton && <div className="gcontent-container feature-top">
                        {
                            <MotiveComponent
                                data="9534307497"
                                containerWidth="100%"
                                containerHeight="90px"
                                location={MotiveLocationTypes.convertPageBottom}
                                adWidth="720px"
                                adHeight="90px"
                                showAds={true}
                                className='width_major_md'
                            ></MotiveComponent>
                        }
                    </div>
                }
            </div>

            {
                this.state.fileSelectedByUser && false && <div className={styles['file-convert-options-and-button-container']}>
                    <div className={styles['side-panel']}>
                        <div className={styles['vertical-file-upload-and-options-toggle-btn-container']}>
                            <div className={styles['toggle-settings-button']}>
                                <Tooltip text="Show/hide settings">
                                    <button className="x-btn x-btn-primary x-btn-svg text-flex-center" onClick={() => this.toggleSideMenu()}>
                                        <img src="/icons/svg/gear.svg" />
                                    </button>
                                </Tooltip>
                            </div>
                            <div className={styles['file-upload-buttons']}>
                                {
                                    !this.state.showMainUploadButton && <FileUploadComponent {...fileUploadProps} displayBtnsInAColumn={true}></FileUploadComponent>
                                }
                            </div>
                        </div>

                        <div className={`${styles['file-convert-options']} ${this.state.showMainUploadButton ? styles['main-btn-shown'] : ''}`}>
                            <ConvertOptionsComponent
                                {...this.props} {...extraProps}></ConvertOptionsComponent>
                        </div>

                        <div className={`${styles['start-file-convert-btn-parent']} ${this.state.showMainUploadButton ? styles['main-btn-shown'] : ''}`}>
                            <button className={`x-btn x-btn-primary ${this.hasFileUploads ? 'x-btn-primary-ripple' : ''} ${styles['btn-lg']} ${styles['convert-btn']} ${this.startConvertProcessTriggered ? 'btn-danger' : ''}`}
                                onClick={() => this.convertWhenReady()}>
                                {
                                    this.startConvertProcessTriggered ?
                                        'Loading...' : <span>{(this.btnText || ((this.processType || 'Process')))}
                                            <img style={{ width: '40px' }} className="color-invert" src="/icons/svg/arrow-right.svg" /></span>
                                }
                            </button>
                        </div>
                    </div>
                </div>
            }

        </div>
    }
}